import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  type EnvironmentProviders,
  PLATFORM_ID,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type Event, NavigationEnd, Router, Scroll } from '@angular/router';
import { filter, pairwise } from 'rxjs';

function getBaseRoute(url: string): string {
  return url.split('?')[0];
}

/**
 * Подписка на Scroll-события роутера: восстановление позиции/якоря при
 * back/forward и скролл в начало при смене базового маршрута. Ручное
 * управление сознательно — Router сконфигурирован с
 * withInMemoryScrolling({ scrollPositionRestoration: 'disabled' }).
 */
function handleScrollOnNavigation(): void {
  const router = inject(Router);
  const destroyRef = inject(DestroyRef);
  const viewportScroller = inject(ViewportScroller);

  router.events
    .pipe(
      filter((event: Event): event is Scroll => event instanceof Scroll),
      pairwise(),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(([prevEvent, currEvent]) => {
      if (currEvent.position) {
        // Backward navigation
        viewportScroller.scrollToPosition(currEvent.position);
      } else if (currEvent.anchor) {
        // Anchor navigation
        viewportScroller.scrollToAnchor(currEvent.anchor);
      } else {
        const prevRouterEvent = prevEvent.routerEvent;
        const prevEventUrl =
          prevRouterEvent instanceof NavigationEnd
            ? prevRouterEvent.urlAfterRedirects
            : prevRouterEvent.url;

        const currRouterEvent = currEvent.routerEvent;
        const currEventUrl =
          currRouterEvent instanceof NavigationEnd
            ? currRouterEvent.urlAfterRedirects
            : currRouterEvent.url;

        if (getBaseRoute(prevEventUrl) !== getBaseRoute(currEventUrl)) {
          viewportScroller.scrollToPosition([0, 0]);
        }
      }
    });
}

/**
 * Глобальное управление скроллом: ручное восстановление истории скролла и
 * подписка на Scroll-события роутера. Только браузер — на сервере
 * ViewportScroller это no-op (NullViewportScroller), а Scroll-событий при
 * SSR-рендере нет. Гард isPlatformBrowser нужен, потому что браузерный конфиг
 * мёржится в серверный (mergeApplicationConfig в app.config.server.ts) и
 * app-initializer иначе выполнился бы и на сервере.
 */
export function provideScrollRestoration(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

      inject(ViewportScroller).setHistoryScrollRestoration('manual');
      handleScrollOnNavigation();
    }),
  ]);
}
